import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../utils/api';
import { Presentation } from 'lucide-react';
import anuKingdomLogo from '../assets/images/anu-kingdom-logo.webp';
import './Auth.css';

const InstructorLogin = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const { login, user } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user) {
      if (user.role === 'instructor' || user.role === 'admin' || user.role === 'sub-admin') {
        navigate('/instructor');
      } else {
        setError('Access denied. Instructor credentials required.');
      }
    }

    // Check for error from OAuth
    const authError = searchParams.get('error');
    if (authError) {
      setError('Google login failed. Please try again.');
    }
  }, [user, navigate, searchParams]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(formData.email, formData.password);

    if (result.success) {
      // Check if user is instructor, admin, or sub-admin
      const userData = JSON.parse(localStorage.getItem('user'));
      if (userData && (userData.role === 'instructor' || userData.role === 'admin' || userData.role === 'sub-admin')) {
        navigate('/instructor');
      } else {
        setError('Access denied. Instructor credentials required.');
        // Logout non-instructor user
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.reload();
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleGoogleLogin = () => {
    // Store intended role for OAuth callback
    localStorage.setItem('intended_role', 'instructor');
    window.location.href = `${API_URL}/auth/google`;
  };

  return (
    <div className="auth-page instructor-auth">
      <Link to="/" className="auth-brand" aria-label="Anu Kingdom home">
        <img src={anuKingdomLogo} alt="Anu Kingdom" />
        <span>ANU KINGDOM</span>
        <small>Learning Platform</small>
      </Link>
      <div className="auth-container">
        <div className="auth-header">
          <div className="role-icon instructor-icon"><Presentation size={25} aria-hidden="true" /></div>
          <h1>Instructor Portal</h1>
          <p>Manage your courses and students</p>
        </div>

        <button onClick={handleGoogleLogin} className="google-btn">
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <div className="divider">
          <span>or</span>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Instructor Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="instructor@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
            />
            <Link to="/forgot-password" className="forgot-password-link">
              Forgot Password?
            </Link>
          </div>

          {error && <div className="error">{error}</div>}

          <button type="submit" className="btn-primary btn-instructor" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In as Instructor'}
          </button>

          <div className="auth-links">
            <Link to="/login" className="back-link">Student Login</Link>
            <Link to="/admin/login" className="back-link">Admin Login</Link>
          </div>

          <p className="auth-link">
            Want to become an instructor? <Link to="/register">Apply here</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default InstructorLogin;
